// =============================================================================
// Admin Contact Detail Page
// =============================================================================
// Detailed view of a CRM contact with activity timeline, subscriptions, and notes
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  PencilIcon,
  ClockIcon,
  TagIcon,
  ChatBubbleLeftIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  ChartBarIcon,
  FolderIcon,
  BuildingOfficeIcon,
  KeyIcon,
  NoSymbolIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError, formatPhoneDisplay } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  ContactWithSubscriptions,
  ContactActivity,
  PipelineStage,
  ContactActivityType,
  ContactDataRoomAccess,
  SubscriptionWithDetails,
} from '../../../types/fundraising';
import { DataRoomAccessModal } from './DataRoomAccessModal';
import { ContactFormModal } from './ContactFormModal';
import { SubscriptionModal } from './SubscriptionModal';

// Import the label maps
import {
  PIPELINE_STAGE_LABELS as STAGE_LABELS,
  LEAD_SOURCE_LABELS as SOURCE_LABELS,
  ACCREDITATION_STATUS_LABELS as ACCRED_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  PROPERTY_TYPE_LABELS,
} from '../../../types/fundraising';

// =============================================================================
// Pipeline Stage Badge Component
// =============================================================================

const STAGE_COLORS: Record<PipelineStage, string> = {
  LEAD: 'bg-gray-100 text-gray-700',
  QUALIFIED: 'bg-blue-100 text-blue-700',
  DATA_ROOM: 'bg-indigo-100 text-indigo-700',
  INTERESTED: 'bg-purple-100 text-purple-700',
  SOFT_COMMIT: 'bg-yellow-100 text-yellow-700',
  HARD_COMMIT: 'bg-orange-100 text-orange-700',
  DOCS_SENT: 'bg-cyan-100 text-cyan-700',
  DOCS_SIGNED: 'bg-teal-100 text-teal-700',
  FUNDED: 'bg-green-100 text-green-700',
  CONVERTED: 'bg-emerald-100 text-emerald-800',
  PASSED: 'bg-red-100 text-red-700',
  INACTIVE: 'bg-gray-100 text-gray-500',
};

function StageBadge({ stage }: { stage: PipelineStage }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STAGE_COLORS[stage]}`}>
      {STAGE_LABELS[stage]}
    </span>
  );
}

// =============================================================================
// Activity Type Icons
// =============================================================================

const ACTIVITY_TYPE_ICONS: Record<ContactActivityType, string> = {
  NOTE_ADDED: 'note',
  EMAIL_SENT: 'email',
  EMAIL_OPENED: 'email',
  EMAIL_CLICKED: 'email',
  CALL_LOGGED: 'phone',
  MEETING_SCHEDULED: 'calendar',
  MEETING_COMPLETED: 'calendar',
  STAGE_CHANGED: 'stage',
  TAG_ADDED: 'tag',
  TAG_REMOVED: 'tag',
  DATA_ROOM_GRANTED: 'document',
  DATA_ROOM_ACCESSED: 'document',
  DOCUMENT_VIEWED: 'document',
  DOCUMENT_DOWNLOADED: 'document',
  INTEREST_INDICATED: 'interest',
  SUBSCRIPTION_CREATED: 'money',
  DOCS_SENT: 'document',
  DOCS_SIGNED: 'document',
  FUNDING_RECEIVED: 'money',
  CONVERTED_TO_INVESTOR: 'success',
  PORTAL_ACCESS_GRANTED: 'success',
  PORTAL_ACCESS_REVOKED: 'tag',
};

// =============================================================================
// Change Stage Modal Component
// =============================================================================

interface ChangeStageModalProps {
  isOpen: boolean;
  currentStage: PipelineStage;
  onClose: () => void;
  onSave: (newStage: PipelineStage) => Promise<void>;
}

const PIPELINE_STAGES_ORDER: PipelineStage[] = [
  'LEAD',
  'QUALIFIED',
  'DATA_ROOM',
  'INTERESTED',
  'SOFT_COMMIT',
  'HARD_COMMIT',
  'DOCS_SENT',
  'DOCS_SIGNED',
  'FUNDED',
  'CONVERTED',
  'PASSED',
  'INACTIVE',
];

function ChangeStageModal({ isOpen, currentStage, onClose, onSave }: ChangeStageModalProps) {
  const [selectedStage, setSelectedStage] = useState<PipelineStage>(currentStage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedStage(currentStage);
      setError(null);
    }
  }, [isOpen, currentStage]);

  const handleSave = async () => {
    if (selectedStage === currentStage) {
      onClose();
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(selectedStage);
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Change Pipeline Stage</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mb-6">
          {PIPELINE_STAGES_ORDER.map((stage) => (
            <button
              key={stage}
              onClick={() => setSelectedStage(stage)}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                selectedStage === stage
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {STAGE_LABELS[stage]}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Add Note Modal Component
// =============================================================================

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}

function AddNoteModal({ isOpen, onClose, onSave }: AddNoteModalProps) {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!note.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(note.trim());
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Note</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4"
          placeholder="Enter your note..."
        />

        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={isSubmitting || !note.trim()}>
            {isSubmitting ? 'Saving...' : 'Add Note'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Activity Item Component
// =============================================================================

interface ActivityItemProps {
  activity: ContactActivity;
}

function ActivityItem({ activity }: ActivityItemProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getIcon = () => {
    const iconType = ACTIVITY_TYPE_ICONS[activity.type] || 'note';
    switch (iconType) {
      case 'email':
        return <EnvelopeIcon className="h-4 w-4" />;
      case 'phone':
        return <PhoneIcon className="h-4 w-4" />;
      case 'tag':
        return <TagIcon className="h-4 w-4" />;
      case 'stage':
        return <ChartBarIcon className="h-4 w-4" />;
      case 'success':
        return <CheckIcon className="h-4 w-4" />;
      default:
        return <ChatBubbleLeftIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex gap-3 py-3">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">{activity.description}</p>
        <p className="text-xs text-gray-500 mt-1">{formatDate(activity.createdAt)}</p>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canEdit = hasPermission('fundraising', 'edit');
  const canCreate = hasPermission('fundraising', 'create');
  const canDelete = hasPermission('fundraising', 'delete');
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState<ContactWithSubscriptions | null>(null);
  const [activities, setActivities] = useState<ContactActivity[]>([]);
  const [dataRoomAccess, setDataRoomAccess] = useState<ContactDataRoomAccess[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'subscriptions' | 'datarooms'>('overview');
  const [showStageModal, setShowStageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDataRoomModal, setShowDataRoomModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithDetails[]>([]);
  const [isGrantingPortalAccess, setIsGrantingPortalAccess] = useState(false);
  const [isRevokingPortalAccess, setIsRevokingPortalAccess] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeConfirmText, setRevokeConfirmText] = useState('');

  const loadContact = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getContact(contactId);
      setContact(data);
    } catch (err) {
      console.error('Failed to load contact:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  const loadActivities = useCallback(async () => {
    if (!contactId) return;
    try {
      const data = await fundraisingService.getContactActivities(contactId);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  }, [contactId]);

  const loadDataRoomAccess = useCallback(async () => {
    if (!contactId) return;
    try {
      const data = await fundraisingService.getContactDataRoomAccess(contactId);
      setDataRoomAccess(data);
    } catch (err) {
      console.error('Failed to load data room access:', err);
    }
  }, [contactId]);

  const loadSubscriptions = useCallback(async () => {
    if (!contactId) return;
    try {
      const data = await fundraisingService.getContactSubscriptions(contactId);
      setSubscriptions(data);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    }
  }, [contactId]);

  useEffect(() => {
    if (contactId) {
      loadContact();
      loadActivities();
      loadDataRoomAccess();
      loadSubscriptions();
    }
  }, [contactId, loadContact, loadActivities, loadDataRoomAccess, loadSubscriptions]);

  const handleStageChange = async (newStage: PipelineStage) => {
    if (!contactId) return;
    await fundraisingService.changePipelineStage(contactId, newStage);
    await loadContact();
    await loadActivities();
  };

  const handleAddNote = async (note: string) => {
    if (!contactId) return;
    await fundraisingService.logActivity(contactId, 'NOTE_ADDED', note);
    await loadActivities();
  };

  const handleGrantPortalAccess = async () => {
    if (!contactId) return;
    setIsGrantingPortalAccess(true);
    try {
      const result = await fundraisingService.grantPortalAccess(contactId, {
        sendWelcomeEmail: true,
        dealIds: dataRoomAccess.filter(a => !a.revokedAt).map(a => a.deal.id),
      });
      if (result.success) {
        await loadContact();
        await loadActivities();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Failed to grant portal access:', err);
      setError(getApiError(err).message);
    } finally {
      setIsGrantingPortalAccess(false);
    }
  };

  const handleRevokePortalAccessClick = () => {
    setRevokeConfirmText('');
    setShowRevokeConfirm(true);
  };

  const handleRevokePortalAccessConfirm = async () => {
    if (!contactId || revokeConfirmText.toLowerCase() !== 'proceed') return;
    setShowRevokeConfirm(false);
    setIsRevokingPortalAccess(true);
    try {
      const result = await fundraisingService.revokePortalAccess(contactId);
      if (result.success) {
        await loadContact();
        await loadActivities();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Failed to revoke portal access:', err);
      setError(getApiError(err).message);
    } finally {
      setIsRevokingPortalAccess(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-200 rounded-xl" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="space-y-6">
        <Link to="/admin/fundraising/contacts" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Contacts
        </Link>
        <Card padding="lg">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {error || 'Contact not found'}
            </h3>
            <Button className="mt-4" onClick={() => navigate('/admin/fundraising/contacts')}>
              Return to Contacts
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const totalCommitment = contact.subscriptions?.reduce((sum, sub) => sum + sub.commitmentAmount, 0) || 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserCircleIcon },
    { id: 'activity', label: 'Activity', icon: ClockIcon },
    { id: 'datarooms', label: 'Data Rooms', icon: FolderIcon },
    { id: 'subscriptions', label: 'Subscriptions', icon: ChartBarIcon },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/fundraising/contacts"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-xl font-semibold text-primary-700">
                {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {contact.firstName} {contact.lastName}
                </h1>
                <StageBadge stage={contact.pipelineStage} />
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <EnvelopeIcon className="h-4 w-4" />
                  {contact.email}
                </span>
                {contact.phone && (
                  <span className="flex items-center gap-1">
                    <PhoneIcon className="h-4 w-4" />
                    {formatPhoneDisplay(contact.phone)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="secondary" onClick={() => setShowStageModal(true)}>
              Change Stage
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => setShowEditModal(true)} className="flex items-center gap-2">
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Lead Source</p>
          <p className="text-lg font-semibold text-gray-900">{SOURCE_LABELS[contact.leadSource]}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Subscriptions</p>
          <p className="text-2xl font-bold text-gray-900">{contact.subscriptions?.length || 0}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Commitment</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalCommitment)}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Activities</p>
          <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <Card padding="lg" className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="text-sm font-medium text-gray-900">{contact.company || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Title</p>
                <p className="text-sm font-medium text-gray-900">{contact.title || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Lead Source</p>
                <p className="text-sm font-medium text-gray-900">{SOURCE_LABELS[contact.leadSource]}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Referred By</p>
                <p className="text-sm font-medium text-gray-900">{contact.referredBy || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Investment Capacity</p>
                <p className="text-sm font-medium text-gray-900">{contact.investmentCapacity || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Accreditation</p>
                <p className="text-sm font-medium text-gray-900">
                  {contact.accreditationStatus ? ACCRED_LABELS[contact.accreditationStatus] : 'Not Verified'}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">
                  {[contact.address, contact.city, contact.state, contact.zipCode].filter(Boolean).join(', ') || '-'}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Property Interests</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {contact.propertyInterests?.length > 0 ? (
                    contact.propertyInterests.map((interest) => (
                      <span key={interest} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {PROPERTY_TYPE_LABELS[interest] || interest}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">None specified</span>
                  )}
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Tags</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {contact.tags?.length > 0 ? (
                  contact.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                      <TagIcon className="h-3 w-3" />
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No tags</span>
                )}
              </div>
            </div>

            {/* Notes */}
            {contact.notes && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
              </div>
            )}
          </Card>

          {/* Timeline Preview */}
          <Card padding="lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              {canEdit && (
                <Button variant="secondary" size="sm" onClick={() => setShowNoteModal(true)}>
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Note
                </Button>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {activities.slice(0, 5).map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
              {activities.length === 0 && (
                <p className="text-sm text-gray-500 py-4 text-center">No activity yet</p>
              )}
              {activities.length > 5 && (
                <div className="pt-3">
                  <button
                    onClick={() => setActiveTab('activity')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View all {activities.length} activities
                  </button>
                </div>
              )}
            </div>

            {/* Key Dates */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Dates</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="font-medium">{formatDate(contact.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Stage Changed</span>
                  <span className="font-medium">{formatDate(contact.stageChangedAt)}</span>
                </div>
                {contact.portalAccessGranted && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Portal Access</span>
                    <span className="font-medium">{formatDate(contact.portalAccessGranted)}</span>
                  </div>
                )}
                {contact.convertedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Converted</span>
                    <span className="font-medium text-green-600">{formatDate(contact.convertedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Portal Access */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Portal Access</h3>
              {contact.userId ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <KeyIcon className="h-4 w-4" />
                    <span className="font-medium">Portal access granted</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    This contact can log in and access their data room documents.
                  </p>
                  {canDelete && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleRevokePortalAccessClick}
                      disabled={isRevokingPortalAccess}
                      className="w-full flex items-center justify-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <NoSymbolIcon className="h-4 w-4" />
                      {isRevokingPortalAccess ? 'Revoking...' : 'Revoke Access'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">
                    Grant portal access to allow this contact to log in and view data room documents.
                  </p>
                  {canEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleGrantPortalAccess}
                      disabled={isGrantingPortalAccess}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <KeyIcon className="h-4 w-4" />
                      {isGrantingPortalAccess ? 'Granting...' : 'Grant Portal Access'}
                    </Button>
                  )}
                  <p className="text-xs text-gray-400">
                    A welcome email will be sent with login instructions.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'activity' && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Activity Timeline</h2>
            {canEdit && (
              <Button variant="secondary" onClick={() => setShowNoteModal(true)}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Note
              </Button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
            {activities.length === 0 && (
              <p className="text-sm text-gray-500 py-8 text-center">No activity yet. Add a note to get started.</p>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'datarooms' && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Data Room Access</h2>
            {canCreate && (
              <Button onClick={() => setShowDataRoomModal(true)}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Grant Access
              </Button>
            )}
          </div>
          {dataRoomAccess.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Deal</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">NDA</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Access</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Granted</th>
                  </tr>
                </thead>
                <tbody>
                  {dataRoomAccess.map((access) => {
                    const isExpired = access.expiresAt && new Date(access.expiresAt) < new Date();
                    const isRevoked = !!access.revokedAt;
                    const isActive = !isRevoked && !isExpired;

                    return (
                      <tr key={access.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                            <Link
                              to={`/admin/deals/${access.deal.id}`}
                              className="text-sm font-medium text-primary-600 hover:text-primary-700"
                            >
                              {access.deal.name}
                            </Link>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              isRevoked
                                ? 'bg-red-100 text-red-700'
                                : isExpired
                                ? 'bg-yellow-100 text-yellow-700'
                                : isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {isRevoked ? 'Revoked' : isExpired ? 'Expired' : 'Active'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {access.ndaRequired ? (
                            access.ndaSignedAt ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                <CheckIcon className="h-3 w-3" />
                                Signed
                              </span>
                            ) : (
                              <span className="text-xs text-yellow-600">Pending</span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400">Not required</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {access.accessCount} views
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {formatDate(access.grantedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderIcon className="h-12 w-12 text-gray-300 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">No data room access granted yet.</p>
              <p className="text-xs text-gray-400">Grant access to allow this contact to view deal documents.</p>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'subscriptions' && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
            {canCreate && (
              <Button onClick={() => setShowSubscriptionModal(true)}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Create Subscription
              </Button>
            )}
          </div>
          {subscriptions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Deal</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Commitment</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Funded</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Entity</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((subscription) => {
                    const statusColors: Record<string, string> = {
                      INTERESTED: 'bg-gray-100 text-gray-700',
                      SOFT_COMMIT: 'bg-yellow-100 text-yellow-700',
                      HARD_COMMIT: 'bg-orange-100 text-orange-700',
                      DOCS_SENT: 'bg-cyan-100 text-cyan-700',
                      DOCS_VIEWED: 'bg-blue-100 text-blue-700',
                      DOCS_SIGNED: 'bg-teal-100 text-teal-700',
                      FUNDING_PENDING: 'bg-purple-100 text-purple-700',
                      FUNDED: 'bg-green-100 text-green-700',
                      CONVERTED: 'bg-emerald-100 text-emerald-800',
                      CANCELLED: 'bg-red-100 text-red-700',
                      EXPIRED: 'bg-gray-100 text-gray-500',
                    };
                    return (
                      <tr key={subscription.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/deals/${subscription.deal.id}`}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                          >
                            {subscription.deal.name}
                          </Link>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[subscription.status] || 'bg-gray-100 text-gray-700'}`}>
                            {SUBSCRIPTION_STATUS_LABELS[subscription.status]}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {subscription.commitmentType === 'HARD' ? 'Hard' : 'Soft'}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                          {formatCurrency(subscription.commitmentAmount)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-gray-500">
                          {subscription.fundedAmount ? formatCurrency(subscription.fundedAmount) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {subscription.entityName || subscription.entityType || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {subscription.commitmentDate ? formatDate(subscription.commitmentDate) : formatDate(subscription.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">No subscriptions yet.</p>
              <p className="text-xs text-gray-400">Create a subscription to track this contact's investment commitment.</p>
            </div>
          )}
        </Card>
      )}

      {/* Modals */}
      <ChangeStageModal
        isOpen={showStageModal}
        currentStage={contact.pipelineStage}
        onClose={() => setShowStageModal(false)}
        onSave={handleStageChange}
      />
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSave={handleAddNote}
      />
      <DataRoomAccessModal
        isOpen={showDataRoomModal}
        contactId={contactId || ''}
        contactName={`${contact.firstName} ${contact.lastName}`}
        existingAccess={dataRoomAccess}
        onClose={() => setShowDataRoomModal(false)}
        onAccessGranted={() => {
          loadDataRoomAccess();
          loadActivities();
        }}
      />
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        contactId={contactId || ''}
        contactName={`${contact.firstName} ${contact.lastName}`}
        existingSubscriptions={subscriptions}
        onClose={() => setShowSubscriptionModal(false)}
        onSubscriptionCreated={async () => {
          await Promise.all([loadSubscriptions(), loadContact(), loadActivities()]);
        }}
      />
      <ContactFormModal
        isOpen={showEditModal}
        contact={contact}
        onClose={() => setShowEditModal(false)}
        onSaved={async () => {
          setShowEditModal(false);
          await loadContact();
        }}
      />

      {/* Revoke Portal Access Confirmation Modal */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <NoSymbolIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Revoke Portal Access</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to revoke portal access for:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {contact.firstName} {contact.lastName}
            </p>
            <p className="text-sm text-red-600 mb-4">
              The user will no longer be able to log in to the portal. This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm:
              </label>
              <input
                type="text"
                value={revokeConfirmText}
                onChange={(e) => setRevokeConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRevokeConfirm(false);
                  setRevokeConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokePortalAccessConfirm}
                disabled={revokeConfirmText.toLowerCase() !== 'proceed'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Revoke Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactDetailPage;
